import React from "react"
import { useSelector } from "react-redux"
import { DndContext, closestCenter } from "@dnd-kit/core"
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable"
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, RadialLinearScale, PointElement,
  LineElement, ArcElement, BarElement, Title, Tooltip, Legend, Filler
} from "chart.js"
import { Button, Typography, Spin, Flex } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useTitle } from "hooks/useTitle"
import styles from "./style.module.css"
import { CHART_TYPES, KPI_KEYS } from "./constants"
import { buildStats, getActivePeriodLabel } from "./utils"
import {
  BookingAvailabilityWidget, RevenueTrendWidget, BookingsByExperienceWidget,
  AnnualSummaryWidget, TopCustomersWidget, TopExperiencesWidget
} from "./widgets"
import { AddWidgetModal, SortableWidget, DashChatWidget, StatsRow, AnalyticsHeader } from "./components"
import useDashboardData from "./hooks/useDashboardData"
import useDashboardWidgets from "./hooks/useDashboardWidgets"

const { Text, Title: AntTitle } = Typography

ChartJS.register(
  CategoryScale, LinearScale, RadialLinearScale, PointElement, LineElement,
  ArcElement, BarElement, Title, Tooltip, Legend, Filler
)

const WIDGET_COMPONENTS = {
  "booking-availability": BookingAvailabilityWidget,
  "annual-summary": AnnualSummaryWidget,
  "revenue-trend": RevenueTrendWidget,
  "top-customers": TopCustomersWidget,
  "bookings-by-experience": BookingsByExperienceWidget,
  "top-experiences": TopExperiencesWidget
}

const WIDGET_DEFAULT_CHART_TYPE = {
  "revenue-trend": CHART_TYPES.LINE,
  "annual-summary": CHART_TYPES.BAR,
  "bookings-by-experience": CHART_TYPES.DONUT,
  "booking-availability": CHART_TYPES.STAT,
  "top-customers": CHART_TYPES.LIST,
  "top-experiences": CHART_TYPES.LIST,
}

export default function DashboardPageV2() {
  useTitle("Dashboard")
  const { t } = useTranslation()
  const { data: { loading: dataLoading }, kpi: { loading: kpiLoading } } = useSelector((state) => state.aiDashboard)

  const {
    widgets, setWidgets,
    dashboardData, kpiData,
    activeDateFilter, setActiveDateFilter,
    dateRange, setDateRange,
    singleDate, setSingleDate,
    hasNonDefault,
    activeInsightTypesRef,
    widgetLoadingApiIds,
    kpiLoadingKeys,
    fetchDashboardData, fetchAllKpis,
    handlePeriodChange
  } = useDashboardData()

  const {
    isEditing, setIsEditing,
    isAddWidgetOpen, setAddWidgetOpen,
    isAddWidgetLoading, isResetLoading,
    sensors,
    handleAddWidget, handleDeleteWidget, handleDragEnd, handleReset
  } = useDashboardWidgets({ activeDateFilter, dateRange, singleDate, fetchDashboardData, fetchAllKpis })

  const [calendarOpen, setCalendarOpen] = React.useState(false)

  const activePeriodLabel = getActivePeriodLabel(activeDateFilter, dateRange, singleDate)

  return (
    <div className={styles.page} style={dataLoading ? { overflow: "hidden" } : undefined}>
      {dataLoading && (
        <Flex align="center" justify="center" className={styles.widgetsLoadingOverlay}><Spin size="large" /></Flex>
      )}

      <Flex justify="space-between" align="center" className={styles.pageHeader}>
        <div>
          <Flex align="center" gap={8} className={styles.titleRow}>
            <AntTitle level={2} className={styles.pageTitle}>{t("ai_dashboard.title")}</AntTitle>
            <span className={styles.aiBadge}>{t("ai_dashboard.ai_powered")}</span>
          </Flex>
          <Text type="secondary">{t("ai_dashboard.subtitle")}</Text>
        </div>
        <Button
          type="primary" icon={<ReloadOutlined />} size="large" shape="round"
          disabled={!hasNonDefault}
          loading={isResetLoading || dataLoading || kpiLoading}
          onClick={handleReset}
        >
          {t("ai_dashboard.reset")}
        </Button>
      </Flex>

      <StatsRow
        stats={buildStats(kpiData, activePeriodLabel)}
        loadingKeys={kpiLoadingKeys}
        kpiKeys={KPI_KEYS}
      />

      <AnalyticsHeader
        activeDateFilter={activeDateFilter} setActiveDateFilter={setActiveDateFilter}
        dateRange={dateRange} setDateRange={setDateRange}
        singleDate={singleDate} setSingleDate={setSingleDate}
        calendarOpen={calendarOpen} setCalendarOpen={setCalendarOpen}
        isEditing={isEditing} setIsEditing={setIsEditing}
        onAddWidget={() => setAddWidgetOpen(true)}
        onPeriodChange={handlePeriodChange}
        hasWidgets={widgets.length > 0}
      />

      <div className={styles.widgetsGridWrapper}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, setWidgets)}>
          <SortableContext items={widgets.map((w) => w.id)} strategy={rectSortingStrategy}>
            <div className={styles.widgetsGrid}>
              {widgets.map((widget) => (
                <SortableWidget
                  key={widget.id}
                  widget={widget}
                  dashboardData={dashboardData}

                  period={activeDateFilter}
                  dateRange={dateRange}
                  isEditing={isEditing}
                  widgetComponents={WIDGET_COMPONENTS}
                  widgetDefaultChartTypes={WIDGET_DEFAULT_CHART_TYPE}
                  onDelete={(apiId) => handleDeleteWidget(apiId, setWidgets)}
                  loading={widgetLoadingApiIds.has(String(widget.apiId))}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      <DashChatWidget onWidgetSaved={() => fetchDashboardData(activeDateFilter, dateRange, singleDate)} />

      <AddWidgetModal
        open={isAddWidgetOpen}
        onClose={() => setAddWidgetOpen(false)}
        onAdd={handleAddWidget}
        loading={isAddWidgetLoading}
        activeInsightTypes={activeInsightTypesRef.current}
      />
    </div>
  )
}
